<template>
  <BottomSheet
    :open="open"
    :back="back"
    :on-closed="onClosed"
    @update:open="$emit('update:open', $event)"
  >
    <template #title>{{ t(editing ? 'income.sheet.title_amend' : 'income.sheet.title') }}</template>
    <template #meta>{{ t('income.sheet.meta') }}</template>

    <!-- First in the sheet (MOL-181). An amendment opens without a connection (MOL-189, С-19): what
         waits for it is said once here, and the buttons say it on themselves. From an account's
         journal there is no removing (В-1 «а»), and the words name only the amendment. -->
    <StatusStrip
      v-if="waiting"
      class="status"
      kind="offline"
      :text="t(removable ? 'income.sheet.offline' : 'income.sheet.offline_amend')"
    />
    <!-- The list read again after a conflict did not come (MOL-188 №5): «Повторить» of the screen is
         under this sheet, so the sheet has its own. -->
    <StatusStrip
      v-else-if="conflict && stale"
      class="status"
      kind="unanswered"
      :text="t('income.sheet.unanswered')"
      :attempt="attempt"
    >
      <template #action>
        <AppButton variant="ghost" @click="$emit('retry')">{{ t('state.retry') }}</AppButton>
      </template>
    </StatusStrip>

    <form ref="form" class="form" @submit.prevent="submit">
      <div class="pair">
        <AppField
          v-model="amount"
          :label="t('income.sheet.amount')"
          kind="decimal"
          :error="amountError"
          enterkeyhint="next"
        />
        <AppField
          v-model="currency"
          :label="t('income.sheet.currency')"
          kind="select"
          :options="currencyOptions"
        />
      </div>
      <!-- Money bought with the currency of conversion has a price the person knows: written as
           an exchange, the rate counts by it rather than by the bank (MOL-66, Р-6). -->
      <p v-if="currency !== overview.base" class="hint">
        {{ t('income.sheet.bought_hint', { base: sign(overview.base) }) }}
      </p>
      <!-- Where it came in, under the pair it is about; only accounts of its currency (06). -->
      <AccountRow
        v-if="showsAccount"
        :label="t('accounts.picker.row_income')"
        :account="account"
        @open="pickerOpen = true"
      />

      <AppField
        v-model="source"
        :label="t('income.sheet.source')"
        kind="select"
        :options="sourceOptions"
        :placeholder="t('income.sheet.source_placeholder')"
        :error-text="sourceMissing ? t('income.sheet.no_source') : null"
      />

      <AppField
        v-model="day"
        :label="t('income.sheet.day')"
        kind="date"
        min="2000-01-02"
        :max="today"
        :error="dayError"
        :error-text="dayInvalid ? t('income.sheet.bad_day') : null"
      />

      <AppReveal>
        <div v-if="showsHeld">
          <AppField
            v-model="held"
            :label="t('income.sheet.held', { currency: sign(currency) })"
            kind="decimal"
            :error="heldError"
            :aria-describedby="`${id}-held`"
          />
          <p :id="`${id}-held`" class="hint">
            {{ t('income.sheet.held_hint', { bank: bankWords(currency, overview.base, t) }) }}
            <template v-if="estimate"> <br />{{ estimate }} </template>
          </p>
          <HeldFromAccounts
            :currency="currency"
            :day="day"
            :except="editing?.id ?? null"
            @fill="held = typed($event)"
          />
        </div>
      </AppReveal>

      <AppField
        v-model="note"
        :label="t('income.sheet.note')"
        :placeholder="t('income.sheet.note_placeholder')"
        :error-text="noteInvalid ? t('income.sheet.bad_note') : null"
        :maxlength="noteMax"
        enterkeyhint="done"
      />

      <section v-if="editing && editing.history.length > 0" class="history">
        <SectionCaption as="h3">{{ t('income.sheet.history') }}</SectionCaption>
        <ul class="versions">
          <li v-for="version in editing.history" :key="version.replacedAt.getTime()">
            {{ versionOf(version) }}
          </li>
        </ul>
      </section>
    </form>

    <template #footer>
      <p v-if="failed || removeFailed" class="failed" role="alert">{{ t('income.failed') }}</p>
      <!-- Read again and not answered — the server or the connection — the version the sheet holds is
           the one from before the conflict: «Сейчас записано» by it would be untrue (MOL-188 №5). -->
      <div v-if="conflict && base" class="failed" role="alert">
        <p class="conflict">
          {{ behind ? t('income.sheet.amend_conflict_stale') : t('income.sheet.amend_conflict') }}
        </p>
        <p v-if="!behind" class="conflict current">{{ currentOf(base) }}</p>
      </div>
      <AppButton
        size="large"
        block
        :busy="sending"
        :busy-label="t('income.sheet.saving')"
        :inactive="waiting || busy"
        @click="submit"
      >
        <template v-if="editing" #icon><IconCheck /></template>
        {{ saveWord }}
      </AppButton>
      <!-- Removing lives here, not on the card (MOL-189, Ф-34) — only where the screen offers
           «Вернуть» after it (owner's В-1 «а»). Not filled: one filled button in a sheet. -->
      <AppButton
        v-if="editing && removable"
        variant="danger-ghost"
        block
        :inactive="waiting || busy || sending"
        @click="askRemove"
      >
        <template #icon><IconDelete /></template>
        {{ waiting ? t('income.sheet.remove_offline') : t('income.sheet.remove') }}
      </AppButton>
    </template>
  </BottomSheet>
  <AccountPickerSheet
    v-model:open="pickerOpen"
    :title="t('accounts.picker.row_income')"
    :accounts="accounts.accounts"
    :currency="currency"
    strict
    :selected="accountId"
    @pick="pick"
  />
</template>

<script lang="ts">
import { computed, defineComponent, ref, useId, watch } from 'vue'
import type { PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import { ApiError } from '@molvia/client'
import {
  ERROR,
  EXCHANGE_NOTE_MAX,
  currencySchema,
  currencySign,
  decimalFromMinor,
  exchangeNoteSchema,
  formatMoney,
  incomeSourceSchema,
  isRateDay,
  parseMoney,
} from '@molvia/model'
import type {
  Currency,
  ErrorCode,
  IncomeAmendBody,
  IncomeBody,
  IncomeSource,
  IncomeView,
  IncomesResponse,
  Money,
} from '@molvia/model'
import IconCheck from '~icons/mdi/check'
import IconDelete from '~icons/mdi/trash-can-outline'
import AccountPickerSheet from '@/components/AccountPickerSheet.vue'
import AccountRow from '@/components/AccountRow.vue'
import AppButton from '@/components/AppButton.vue'
import AppField from '@/components/AppField.vue'
import AppReveal from '@/components/AppReveal.vue'
import BottomSheet from '@/components/BottomSheet.vue'
import HeldFromAccounts from '@/components/HeldFromAccounts.vue'
import SectionCaption from '@/components/SectionCaption.vue'
import StatusStrip from '@/components/StatusStrip.vue'
import { bankWords } from '@/components/spending'
import { useAccountChoice } from '@/composables/useAccountChoice'
import { useFieldRefusal } from '@/composables/useFieldRefusal'
import type { AmendOutcome } from '@/composables/useExchanges'
import { shown } from '@/composables/useItemDetails'
import { calendarDayOfAnyYear, dateOfAnyYear, localDay } from '@/days'
import { newId } from '@/ids'
import type { IncomeDraft } from '@/composables/useIncomes'
import { incomesOf } from '@/composables/useIncomes'
import { useLocalDay } from '@/composables/useLocalDay'
import { useOnline } from '@/composables/useOnline'
import { useAccountsStore } from '@/stores/accounts'

/**
 * «Записать доход» (MOL-66): how much came in, in which currency, from where and on which day. What
 * was held before is asked only where it weighs anything — a currency other than the one of
 * conversion that already has a price that day (В-1, as Р-2 of MOL-42 for an exchange).
 *
 * The same sheet amends an income: opened on a row, it starts from what the row says, shows the
 * versions before it and saves over the version it was opened on. Not through a queue: an income
 * is written with a connection, and the identifier is made once per opening, so «Сохранить» again
 * after a failure is the same income.
 */
export default defineComponent({
  name: 'IncomeSheet',
  components: {
    AccountPickerSheet,
    AccountRow,
    AppButton,
    AppField,
    AppReveal,
    BottomSheet,
    HeldFromAccounts,
    IconCheck,
    IconDelete,
    SectionCaption,
    StatusStrip,
  },
  props: {
    open: { type: Boolean, required: true },
    /** Opened over another sheet — a check, «не попали» (MOL-123): «‹» back to it, no ×. */
    back: { type: Boolean, default: false },
    overview: { type: Object as PropType<IncomesResponse>, required: true },
    record: {
      type: Function as PropType<(body: IncomeBody) => Promise<unknown>>,
      required: true,
    },
    amend: {
      type: Function as PropType<(id: string, body: IncomeAmendBody) => Promise<AmendOutcome>>,
      required: true,
    },
    /** The income being amended, or null for a new one. */
    editing: { type: Object as PropType<IncomeView | null>, default: null },
    /**
     * A new income already filled — «Записать разницу» of a check (MOL-123, owner's decision В-5):
     * the sum, «Прочее», «сверка», the account and the day, and the person still answers «сколько
     * было до», which an income written without it would take as the price of the whole currency.
     */
    draft: { type: Object as PropType<IncomeDraft | null>, default: null },
    /** The screen offers «Вернуть» after a removal, so the amendment may offer removing (В-1 «а»). */
    removable: { type: Boolean, default: false },
    /** A write of the screen in the air — a removal, «Вернуть»: the buttons wait for it (Р-3). */
    busy: { type: Boolean, default: false },
    /** The list read again failed, the server or the connection: it is the one from before. */
    behind: { type: Boolean, default: false },
    /** …and the connection is up: the server did not answer, and «Повторить» may help. */
    stale: { type: Boolean, default: false },
    /** `@closed`: the sheet is put away — `BottomSheet`'s, passed on (a prop there, for its reasons). */
    onClosed: { type: Function as PropType<() => void>, default: undefined },
    /**
     * «Удалить» did not reach the server, its question put away meanwhile: said here, the sheet still up
     * — the screen's word is under it (MOL-189, adversarial В2).
     */
    removeFailed: { type: Boolean, default: false },
    /** Reads that failed so far: a new one says the strip's same words again (MOL-181, C1). */
    attempt: { type: Number, default: 0 },
  },
  emits: {
    'update:open': (open: boolean) => typeof open === 'boolean',
    /** «Удалить доход» — the income as the server holds it, to ask «Удалить доход?» over. */
    remove: (income: IncomeView) => typeof income === 'object',
    retry: () => true,
    /**
     * «Сохранить» pressed past its guard — the sheet's next write, whatever it meets, a field refused
     * included: a word about an earlier one, `removeFailed`, goes with it, as the sheet's own does.
     */
    save: () => true,
  },
  setup(props, { emit }) {
    const { t, locale } = useI18n()
    const id = useId()
    const form = ref<HTMLFormElement | null>(null)
    // A field refused is heard (MOL-235, MOL-333): the first one marked in the form, the server's
    // refusal of the day too — never into a sheet swept away while the answer was on its way.
    const refuse = useFieldRefusal()
    function refuseField(): void {
      if (props.open)
        void refuse(() => form.value?.querySelector<HTMLElement>('[aria-invalid="true"]'))
    }

    const amount = ref('')
    const currency = ref<Currency>('RUB')
    const source = ref<IncomeSource | ''>('')
    const amountError = ref<ErrorCode | null>(null)
    const sourceMissing = ref(false)
    // The sheet's day moves as the app comes back and at midnight, so its words follow it while the day it
    // opened on stays the record's (MOL-329, adversarial А2, А3).
    const today = useLocalDay()
    const day = ref(today.value)
    const held = ref('')
    const note = ref('')
    const noteInvalid = ref(false)
    const dayError = ref<ErrorCode | null>(null)
    const dayInvalid = ref(false)
    const heldError = ref<ErrorCode | null>(null)
    const sending = ref(false)
    const failed = ref(false)
    const conflict = ref(false)
    /**
     * A conflict was met at this opening: from then on the version is the list's, whatever the next
     * «Сохранить» met — a refusal under a field, an answer lost — until the sheet opens again. Let go
     * with `conflict`, the words, a refused save went back over the version opened (MOL-189 №1).
     */
    const rebased = ref(false)
    let incomeId = newId()
    const online = useOnline()
    /**
     * An amendment waits for the connection, and says so (MOL-189, С-19, Ф-6) — never while it is at
     * work (MOL-225, Р1-А3). A new income is never opened offline, and is left as it was.
     */
    const waiting = computed(() => !!props.editing && !online.value && !sending.value)
    /**
     * The version an amendment goes over: the one opened, and after a conflict the one the list now
     * holds — still the old one when it was read again and not answered, which `behind` says (MOL-188
     * №5, MOL-187 №8).
     */
    const base = computed(() => {
      const editing = props.editing
      if (!editing || !rebased.value) return editing
      return incomesOf(props.overview).find((row) => row.id === editing.id) ?? editing
    })
    const saveWord = computed(() => {
      if (waiting.value) return t('income.sheet.save_offline')
      return t(props.editing ? 'income.sheet.save_amend' : 'income.sheet.save')
    })
    function askRemove(): void {
      if (base.value) emit('remove', base.value)
    }
    const accounts = useAccountsStore()
    const choice = useAccountChoice(currency, true)
    const pickerOpen = ref(false)

    const typed = (value: Money): string =>
      shown(decimalFromMinor(value), locale.value === 'ru' ? ',' : '.')

    // Made afresh at every opening: the last income's numbers, or its error, are not this one's.
    watch(
      () => props.open,
      (open) => {
        if (!open) return
        const editing = props.editing
        const draft = editing ? null : props.draft
        const filled = editing?.amount ?? draft?.amount ?? null
        amount.value = filled ? typed(filled) : ''
        // Most money comes in the currency it is counted in — the salary of the owner does.
        currency.value = filled?.currency ?? props.overview.base
        source.value = editing?.source ?? draft?.source ?? ''
        amountError.value = null
        sourceMissing.value = false
        today.value = localDay()
        day.value = editing?.receivedOn ?? draft?.receivedOn ?? today.value
        held.value = editing?.heldBefore ? typed(editing.heldBefore) : ''
        note.value = editing?.note ?? draft?.note ?? ''
        noteInvalid.value = false
        dayError.value = null
        dayInvalid.value = false
        heldError.value = null
        failed.value = false
        conflict.value = false
        rebased.value = false
        incomeId = draft?.id ?? newId()
        pickerOpen.value = false
        choice.reset(editing ? editing.accountId : draft ? draft.accountId : undefined)
      },
      { immediate: true },
    )

    const currencyOptions = currencySchema.options.map((value) => ({
      value,
      label: `${currencySign(value, locale.value)} ${value}`,
    }))
    const sourceOptions = computed(() =>
      incomeSourceSchema.options.map((value) => ({
        value,
        label: t(`income.source.${value}`),
      })),
    )
    const sign = (value: Currency) => currencySign(value, locale.value)

    /** The money into `currency`, exchanges and incomes, newest first — this income aside. */
    const into = (value: Currency) =>
      props.overview.receipts.filter(
        (receipt) => receipt.currency === value && receipt.id !== props.editing?.id,
      )

    /**
     * Asked only where it weighs anything (MOL-66, В-1): the income is not in the currency of
     * conversion, which always costs one; that currency already has a price on the chosen day —
     * the latest money into it gave one (`priced`, from the server's walk); and the bank may value
     * this income — on or after the day the currency of conversion was chosen, since before it the
     * bank is never asked. A choice of field, not a computation: the flags are the server's.
     */
    const asksHeld = computed(() => {
      const { base, baseSince } = props.overview
      if (currency.value === base) return false
      if (!into(currency.value).find(({ on }) => on <= day.value)?.priced) return false
      return baseSince === null || day.value >= baseSince
    })

    /** The hint is about the latest money in, so it fits only a day not before it. */
    const estimate = computed(() => {
      if (props.editing) return null
      const hint = props.overview.heldEstimates.find(({ held }) => held.currency === currency.value)
      const latest = into(currency.value).at(0)?.on
      if (!hint || !asksHeld.value || (latest !== undefined && day.value < latest)) return null
      const value = formatMoney(hint.held, locale.value)
      if (hint.whole) return t('exchange.sheet.held_estimate', { amount: value })
      return t(
        hint.from === 'income'
          ? 'exchange.sheet.held_estimate_last_income'
          : 'exchange.sheet.held_estimate_last',
        { amount: value },
      )
    })

    /** A remainder the income already has is shown and kept while its currency is the one. */
    const showsHeld = computed(
      () =>
        asksHeld.value ||
        (!!props.editing?.heldBefore && currency.value === props.editing.amount.currency),
    )

    /** «до 25 сент.: 99 615 ₽ · Зарплата · 15 сент. · Викаса» */
    function versionOf(version: IncomeView['history'][number]): string {
      const words = {
        // Always a date, its year when another (adversarial Г2): «до» puts its own word before the day, and
        // «до вчера» is no Russian (adversarial Д1, the rule of «Раньше …», MOL-204 Р-9).
        until: dateOfAnyYear(version.replacedAt, locale.value, today.value),
        amount: formatMoney(version.amount, locale.value),
        source: t(`income.source.${version.source}`),
        date: calendarDayOfAnyYear(version.receivedOn, locale.value, today.value),
      }
      return version.note
        ? t('income.sheet.version_note', { ...words, note: version.note })
        : t('income.sheet.version', words)
    }

    /** «Сейчас записано: 99 615 ₽ · Зарплата · 15 сент. · было до поступления … · Викаса» */
    function currentOf(income: IncomeView): string {
      const details = [
        formatMoney(income.amount, locale.value),
        t(`income.source.${income.source}`),
        calendarDayOfAnyYear(income.receivedOn, locale.value, today.value),
        ...(income.heldBefore
          ? [
              t('income.sheet.current_held', {
                amount: formatMoney(income.heldBefore, locale.value),
              }),
            ]
          : []),
        ...(income.note ? [income.note] : []),
      ]
      return t('income.sheet.current', { details: details.join(' · ') })
    }

    /** UX only: the server reads the same codecs and has the last word (CLAUDE.md). */
    function money(text: string, value: Currency): Money | null {
      try {
        return parseMoney(text, value)
      } catch {
        return null
      }
    }

    async function submit(): Promise<void> {
      // The field's «done» submits the form too, past the button that is not now.
      if (sending.value || waiting.value || props.busy) return
      failed.value = false
      emit('save')
      dayError.value = null
      heldError.value = null
      dayInvalid.value = !isRateDay(day.value)
      const parsed = money(amount.value, currency.value)
      amountError.value = parsed && parsed.minor > 0n ? null : ERROR.INVALID_AMOUNT
      const chosen = source.value
      sourceMissing.value = chosen === ''
      const heldBefore =
        showsHeld.value && held.value.trim() ? money(held.value, currency.value) : null
      if (showsHeld.value && held.value.trim() && !heldBefore) {
        heldError.value = ERROR.INVALID_AMOUNT
      }
      const typedNote = note.value.trim() ? exchangeNoteSchema.safeParse(note.value) : null
      noteInvalid.value = typedNote !== null && !typedNote.success
      if (
        !parsed ||
        amountError.value ||
        chosen === '' ||
        heldError.value ||
        dayInvalid.value ||
        noteInvalid.value
      ) {
        refuseField()
        return
      }

      const fields = {
        amount: parsed,
        receivedOn: day.value,
        source: chosen,
        ...(heldBefore ? { heldBefore } : {}),
        ...(typedNote?.success ? { note: typedNote.data } : {}),
        // Said whenever there are accounts: left out, the server keeps the one it has (Р-26).
        ...(choice.shows.value ? { accountId: choice.accountId.value } : {}),
      }
      // After a conflict, the version the list now holds.
      const editing = base.value
      sending.value = true
      conflict.value = false
      try {
        if (editing) {
          const outcome = await props.amend(editing.id, { revision: editing.revision, ...fields })
          // Made over a version that moved on: what was typed stays, and the next «Сохранить»
          // goes over the version the screen now holds.
          if (outcome === 'conflict') {
            conflict.value = true
            rebased.value = true
            return
          }
        } else {
          await props.record({ id: incomeId, ...fields })
        }
        emit('update:open', false)
      } catch (caught) {
        if (caught instanceof ApiError && caught.code === ERROR.INCOME_IN_FUTURE) {
          dayError.value = ERROR.INCOME_IN_FUTURE
          refuseField()
        } else {
          failed.value = true
        }
      } finally {
        sending.value = false
      }
    }

    return {
      bankWords,
      accounts,
      accountId: choice.accountId,
      account: choice.account,
      showsAccount: choice.shows,
      pickerOpen,
      pick: choice.pick,
      typed,
      t,
      id,
      form,
      amount,
      currency,
      source,
      amountError,
      sourceMissing,
      currencyOptions,
      sourceOptions,
      sign,
      today,
      day,
      dayError,
      dayInvalid,
      held,
      heldError,
      note,
      noteInvalid,
      noteMax: EXCHANGE_NOTE_MAX,
      versionOf,
      currentOf,
      asksHeld,
      showsHeld,
      estimate,
      conflict,
      waiting,
      base,
      saveWord,
      askRemove,
      sending,
      failed,
      submit,
    }
  },
})
</script>

<style scoped lang="scss">
.status {
  margin-bottom: var(--space-4);
}

.form {
  /* It measures itself for the pairs below (MOL-321). */
  container-type: inline-size;

  /* A column, not a grid: a block of it grows and goes by `AppReveal`, which takes a flex
     column's gap back with the block — a grid's stays and jumped (MOL-151, adversarial Б2). */
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.pair {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
  gap: var(--space-3);
}

/* Narrower than this the sum and its currency stand one under the other (MOL-321, adversarial И2, И3): side by side
   at 200 % the sum had 89 px — «125403» read «1254» — and the currency under its chevron none, «₽ RUB» unseen. In
   rem, as every turn of large text: the form is 18rem at 100 % on 320 and keeps the pair. */
$pair-beside: 15rem;

@container (width < #{$pair-beside}) {
  .pair {
    grid-template-columns: minmax(0, 1fr);
  }
}

.hint {
  margin: var(--space-2) 0 0;
  color: var(--text-muted);
  font-size: var(--text-footnote);
}

.versions {
  display: grid;
  gap: var(--space-2);
  margin: 0;
  padding: 0 0 0 var(--space-3);
  border-left: var(--hairline) solid var(--border-strong);
  color: var(--text-muted);
  font-size: var(--text-footnote);
  list-style: none;
  overflow-wrap: anywhere;
}

.conflict {
  margin: 0;
  overflow-wrap: anywhere;
}

.current {
  margin-top: var(--space-1);
  font-weight: var(--weight-medium);
}

.failed {
  margin: 0 0 var(--space-3);
  color: var(--bad-ink);
  font-size: var(--text-footnote);
}
</style>
